import { useState } from 'react';

function NotifyModal({ onClose }: { onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d6iqln1bfjp31emakb20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[#111111] border border-gray-700 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-notification-3-line text-teal-400 text-lg"></i>
            <h3 className="text-white font-bold text-base">오픈 예정 알림 신청</h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="px-5 py-5">
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-check-line text-teal-400 text-2xl"></i>
              </div>
              <p className="text-white font-bold text-base mb-1">신청 완료!</p>
              <p className="text-gray-400 text-sm">오픈 시 이메일로 알려드릴게요 😊</p>
              <button onClick={onClose} className="mt-4 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap">
                닫기
              </button>
            </div>
          ) : (
            <>
              <div className="mb-4 bg-white/5 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-lock-line text-gray-500 text-sm mt-0.5 flex-shrink-0"></i>
                <p className="text-gray-400 text-xs leading-relaxed">
                  <span className="text-white font-semibold">Smart Plan · Pro Plan</span> 오픈 시 가장 먼저 알림을 받아보세요!
                </p>
              </div>
              <form id="notify-form" data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">이름</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="이름을 입력해주세요"
                    className="w-full bg-white/5 border border-gray-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-teal-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">이메일</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="이메일을 입력해주세요"
                    className="w-full bg-white/5 border border-gray-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-teal-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">관심 플랜</label>
                  <select
                    name="plan"
                    required
                    className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500 transition-colors cursor-pointer"
                  >
                    <option value="">선택해주세요</option>
                    <option value="Smart Plan">Smart Plan</option>
                    <option value="Pro Plan">Pro Plan</option>
                    <option value="Smart Plan · Pro Plan 모두">Smart Plan · Pro Plan 모두</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold py-3 rounded-xl text-sm transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? '신청 중...' : '알림 신청하기'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface PaymentModalProps {
  onClose: () => void;
  onShowHowTo?: () => void;
}

export default function PaymentModal({ onClose, onShowHowTo }: PaymentModalProps) {
  const [copied, setCopied] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [selectedPack, setSelectedPack] = useState<'core' | 'double' | 'master'>('double');
  const [depositorName, setDepositorName] = useState('');
  const [showComplete, setShowComplete] = useState(false);

  const vatRate = 1.1;

  const coreBasePrice = 59000;
  const doubleBasePrice = Math.round(59000 * 0.95);
  const masterBasePrice = Math.round(59000 * 0.90);

  const packs = {
    core: {
      id: 'core',
      name: 'LEXO 코어팩',
      subtitle: 'Vol.1',
      label: '단품',
      badge: null,
      monthlyPrice: coreBasePrice,
      totalPrice: coreBasePrice * 1,
      vatPrice: Math.round(coreBasePrice * 1 * vatRate),
      months: 1,
      discount: null,
      color: 'teal',
      icon: 'ri-book-open-line',
      duration: '첫 열람부터 1개월간 열람',
      versions: [
        { vol: 'Vol.1', open: '결제 확인 후 노션 링크 발송', duration: '첫 열람부터 1개월간 열람 가능' },
      ],
      features: ['Vol.1 노션 링크 제공', '밤 12시 전후 기준 발송', '첫 열람부터 1개월간 열람 가능'],
    },
    double: {
      id: 'double',
      name: 'LEXO 더블팩',
      subtitle: 'Vol.1+2',
      label: '추천',
      badge: '추천',
      monthlyPrice: doubleBasePrice,
      totalPrice: doubleBasePrice * 3,
      vatPrice: Math.round(doubleBasePrice * 3 * vatRate),
      months: 3,
      discount: '5% 할인',
      color: 'orange',
      icon: 'ri-stack-line',
      duration: '첫 열람부터 2개월간 열람',
      versions: [
        { vol: 'Vol.1', open: '결제 확인 후 노션 링크 발송', duration: '첫 열람부터 2개월간 열람 가능' },
        { vol: 'Vol.2', open: 'Vol.1 수강 개시 후 2주 후부터 노션 링크 발송', duration: '첫 열람부터 2개월간 열람 가능' },
      ],
      features: ['Vol.1+2 노션 링크 순차 제공', 'Vol.2는 Vol.1 개시 2주 후 발송', '첫 열람부터 2개월간 열람 가능'],
    },
    master: {
      id: 'master',
      name: 'LEXO 마스터팩',
      subtitle: 'Vol.1+2+3',
      label: 'VIP',
      badge: 'VIP',
      monthlyPrice: masterBasePrice,
      totalPrice: masterBasePrice * 6,
      vatPrice: Math.round(masterBasePrice * 6 * vatRate),
      months: 6,
      discount: '10% 할인',
      color: 'amber',
      icon: 'ri-vip-crown-line',
      duration: '첫 열람부터 3개월간 열람',
      versions: [
        { vol: 'Vol.1', open: '결제 확인 후 노션 링크 발송', duration: '첫 열람부터 3개월간 열람 가능' },
        { vol: 'Vol.2', open: 'Vol.1 수강 개시 후 2주 후부터 노션 링크 발송', duration: '첫 열람부터 3개월간 열람 가능' },
        { vol: 'Vol.3', open: 'Vol.1 수강 개시 후 1달 후부터 노션 링크 발송', duration: '첫 열람부터 3개월간 열람 가능' },
      ],
      features: ['Vol.1+2+3 노션 링크 순차 제공', 'Vol.2는 2주 후, Vol.3는 1달 후 발송', '첫 열람부터 3개월간 열람 가능'],
    },
  };

  const selected = packs[selectedPack];
  const formattedVatPrice = `₩${selected.vatPrice.toLocaleString()}`;
  const formattedMonthlyPrice = `₩${selected.monthlyPrice.toLocaleString()}`;
  const formattedTotalPrice = `₩${selected.totalPrice.toLocaleString()}`;

  const colorMap: Record<string, { border: string; bg: string; text: string; badge: string; btn: string; ring: string }> = {
    teal:   { border: 'border-teal-500',   bg: 'bg-teal-500/10',   text: 'text-teal-400',   badge: 'bg-teal-500 text-white',   btn: 'from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700', ring: 'ring-teal-500' },
    orange: { border: 'border-orange-500', bg: 'bg-orange-500/10', text: 'text-orange-400', badge: 'bg-orange-500 text-white', btn: 'from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600', ring: 'ring-orange-500' },
    amber:  { border: 'border-amber-400',  bg: 'bg-amber-500/10',  text: 'text-amber-400',  badge: 'bg-amber-400 text-black',  btn: 'from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600', ring: 'ring-amber-400' },
  };

  const handleCopy = () => {
    navigator.clipboard.writeText('110621532440');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {showNotify && <NotifyModal onClose={() => setShowNotify(false)} />}
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto pt-16 pb-4 px-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
        <div className="relative bg-[#111111] border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden my-auto">

          {/* 헤더 */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-bank-card-line text-white text-lg"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg leading-tight">수강 신청 안내</h2>
                  <p className="text-teal-100 text-sm">무통장 입금으로 간편하게 시작하세요</p>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="mt-3 bg-white/20 border border-white/30 rounded-xl px-4 py-3 flex items-center justify-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-vip-crown-2-line text-white text-base"></i>
              </div>
              <div className="text-center">
                <p className="text-white font-black text-xl tracking-wide">LEXO Basic 플랜</p>
                <p className="text-teal-100 text-sm font-medium">현재 구매 가능한 플랜</p>
              </div>
            </div>

            <div className="mt-2 flex flex-col gap-1.5">
              <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-2">
                <i className="ri-lock-line text-teal-100 text-sm flex-shrink-0"></i>
                <p className="text-teal-50 text-sm leading-snug">Smart · Pro 플랜은 준비 중입니다.</p>
              </div>
              <div className="flex items-center gap-2 bg-orange-500/30 border border-orange-400/40 rounded-lg px-3 py-2">
                <i className="ri-flashlight-fill text-orange-300 text-sm flex-shrink-0"></i>
                <p className="text-orange-100 text-sm leading-snug">
                  <span className="font-black text-white">이벤트 할인가 적용 중</span> · 정상가 ₩78,000 → <span className="text-orange-300 font-black">₩59,000</span> (부가세 별도)
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-4">

            {/* 패키지 선택 */}
            <div>
              <p className="text-gray-300 font-semibold text-base flex items-center gap-2 mb-3">
                <i className="ri-gift-line text-teal-400"></i>
                패키지 선택
                <span className="text-xs text-gray-400 font-semibold bg-white/10 px-2 py-0.5 rounded-full whitespace-nowrap">전 패키지 이벤트가 적용</span>
              </p>
              <div className="grid grid-cols-3 gap-2">
                {(Object.values(packs) as typeof packs[keyof typeof packs][]).map((pack) => {
                  const c = colorMap[pack.color];
                  const isSelected = selectedPack === pack.id;
                  return (
                    <button
                      key={pack.id}
                      onClick={() => setSelectedPack(pack.id as 'core' | 'double' | 'master')}
                      className={`relative flex flex-col items-center justify-center py-4 px-2 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                        isSelected ? `${c.border} ${c.bg}` : 'border-gray-700 bg-white/5 hover:border-gray-500'
                      }`}
                    >
                      {pack.badge && (
                        <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs font-black px-2 py-0.5 rounded-full whitespace-nowrap ${c.badge}`}>
                          {pack.badge}
                        </span>
                      )}
                      <div className={`w-8 h-8 flex items-center justify-center mb-1.5 ${isSelected ? c.text : 'text-gray-500'}`}>
                        <i className={`${pack.icon} text-xl`}></i>
                      </div>
                      <span className={`font-bold text-sm text-center leading-tight ${isSelected ? 'text-white' : 'text-gray-400'}`}>{pack.name}</span>
                      <span className={`text-xs font-semibold mt-0.5 ${isSelected ? c.text : 'text-gray-600'}`}>{pack.subtitle}</span>
                      <span className={`text-xs mt-1 ${isSelected ? 'text-gray-300' : 'text-gray-600'}`}>{pack.duration}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 선택된 패키지 상세 */}
            <div className={`rounded-xl border ${colorMap[selected.color].border} ${colorMap[selected.color].bg} p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <i className={`${selected.icon} ${colorMap[selected.color].text} text-lg`}></i>
                <span className={`font-bold text-base ${colorMap[selected.color].text}`}>{selected.name} {selected.subtitle}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colorMap[selected.color].badge}`}>{selected.label}</span>
              </div>
              <div className="space-y-2">
                {selected.versions.map((v, i) => (
                  <div key={i} className="bg-black/30 rounded-lg px-3 py-2.5 flex items-start gap-2.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${colorMap[selected.color].bg} border ${colorMap[selected.color].border}`}>
                      <span className={`text-xs font-black ${colorMap[selected.color].text}`}>{i + 1}</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-bold mb-0.5">{v.vol}</p>
                      <p className="text-gray-400 text-sm leading-relaxed">{v.open}</p>
                      <p className={`text-sm font-semibold mt-0.5 ${colorMap[selected.color].text}`}>{v.duration}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 입금 금액 */}
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-gray-400 text-sm">입금 금액 (부가세 포함)</p>
                <span className="text-xs font-bold text-gray-400 bg-white/10 border border-gray-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                  <i className="ri-price-tag-3-line mr-1"></i>이벤트 할인가
                </span>
              </div>
              <div className="space-y-1.5 mb-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">단가 (부가세 별도)</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-600 text-sm line-through">₩78,000</span>
                    <span className="text-gray-300 text-sm font-semibold">{formattedMonthlyPrice}</span>
                  </div>
                </div>
                {selected.discount && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">할인</span>
                    <span className="text-green-400 text-sm font-semibold">{selected.discount} 적용</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">소계 (부가세 별도)</span>
                  <span className="text-gray-300 text-sm font-semibold">{formattedTotalPrice}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">부가세 (VAT 10%)</span>
                  <span className="text-gray-300 text-sm font-semibold">+₩{Math.round(selected.totalPrice * 0.1).toLocaleString()}</span>
                </div>
              </div>
              <div className="border-t border-teal-500/20 pt-3">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <span className={`font-bold text-base ${colorMap[selected.color].text}`}>최종 입금 금액</span>
                  <span className="text-3xl font-black text-white">{formattedVatPrice}</span>
                </div>
                <p className="text-teal-400/70 text-xs mt-1.5 flex items-center gap-1">
                  <i className="ri-information-line text-xs"></i>
                  부가세(VAT 10%) 포함 최종 입금 금액
                </p>
              </div>
            </div>

            {/* 계좌 정보 */}
            <div className="bg-white/5 rounded-xl p-4 space-y-3">
              <p className="text-gray-300 font-semibold text-base flex items-center gap-2">
                <i className="ri-bank-line text-teal-400"></i>
                입금 계좌 정보
              </p>
              <div className="space-y-2.5">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">은행</span>
                  <span className="text-white text-sm font-semibold">신한은행</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">예금주</span>
                  <span className="text-white text-sm font-semibold">이송희</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">계좌번호</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-semibold tracking-wide">110621532440</span>
                    <button
                      onClick={handleCopy}
                      className="flex items-center gap-1 bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 text-xs px-2 py-1 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                      {copied ? '복사됨' : '복사'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-700/50">
                <label className="text-gray-400 text-sm mb-1.5 flex items-center gap-1">
                  <i className="ri-user-line text-teal-400"></i>
                  입금자명
                </label>
                <input
                  type="text"
                  value={depositorName}
                  onChange={(e) => setDepositorName(e.target.value)}
                  placeholder="입금자 이름을 입력하세요"
                  className="w-full bg-[#0F0F0F] border border-gray-700 focus:border-teal-500 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none transition-colors"
                />
              </div>
            </div>

            {/* 진행 순서 */}
            <div className="space-y-2">
              <p className="text-gray-300 font-semibold text-base flex items-center gap-2">
                <i className="ri-list-ordered text-teal-400"></i>
                진행 순서
              </p>
              <div className="space-y-2">
                {[
                  { step: '1', text: `위 계좌로 ${formattedVatPrice} 입금`, icon: 'ri-bank-card-line' },
                  { step: '2', text: 'LEXO 일본어 카카오톡 채널 추가 후 입금 완료 연락', icon: 'ri-kakao-talk-fill' },
                  { step: '3', text: '확인 후 밤 12시 전후 기준으로 학습 링크 발송', icon: 'ri-notion-fill' },
                ].map((item) => (
                  <div key={item.step} className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">{item.step}</span>
                    </div>
                    <span className="text-gray-300 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 카카오톡 안내 메시지 */}
            <div className="bg-[#FEE500]/10 border border-[#FEE500]/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <i className="ri-kakao-talk-fill text-[#FEE500] text-base"></i>
                  <span className="text-[#FEE500] font-bold text-sm">카카오톡 채널 입금 안내 메시지 예시</span>
                </div>
                <button
                  onClick={() => {
                    const msg = `안녕하세요! LEXO 일본어 수강 신청합니다 😊\n\n플랜: LEXO Basic 플랜\n패키지: ${selected.name} ${selected.subtitle}\n입금 금액: ${formattedVatPrice}\n입금자명: ${depositorName || '(입금자명 입력 후 자동 반영)'}\n\n입금 완료했습니다. 확인 부탁드립니다!`;
                    navigator.clipboard.writeText(msg);
                    setMessageCopied(true);
                    setTimeout(() => setMessageCopied(false), 2000);
                  }}
                  className="flex items-center gap-1 bg-[#FEE500]/20 hover:bg-[#FEE500]/40 text-[#3A1D1D] text-xs px-2.5 py-1 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={messageCopied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  {messageCopied ? '복사됨 ✓' : '메시지 복사'}
                </button>
              </div>
              <div className="bg-black/30 rounded-lg p-3 font-mono text-sm text-gray-200 leading-relaxed whitespace-pre-line">
                {`안녕하세요! LEXO 일본어 수강 신청합니다 😊\n\n플랜: LEXO Basic 플랜\n패키지: ${selected.name} ${selected.subtitle}\n입금 금액: ${formattedVatPrice}\n입금자명: ${depositorName || '(입금자명 입력 후 자동 반영)'}\n\n입금 완료했습니다. 확인 부탁드립니다!`}
              </div>
              <p className="text-gray-500 text-xs mt-2">* 위 내용을 복사하여 카카오톡 채널에 보내주세요</p>
            </div>

            <a
              href="http://pf.kakao.com/_exilzX"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setTimeout(() => setShowComplete(true), 500)}
              className="flex items-center justify-center gap-2 w-full py-4 bg-[#FEE500] hover:bg-[#F5DC00] text-[#3A1D1D] font-bold rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap text-base"
            >
              <i className="ri-kakao-talk-fill text-lg"></i>
              LEXO 일본어 카카오톡 채널 추가하기
            </a>

            {/* 환불 정책 */}
            <div className="bg-white/5 border border-gray-700/50 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-information-line text-gray-400 text-xs"></i>
                <span className="text-gray-400 font-semibold text-xs">환불 정책 및 주의사항</span>
              </div>
              <ul className="space-y-1">
                {[
                  '결제 후 7일 이내, 콘텐츠 미사용 시 환불 가능',
                  '노션 링크 열람 후에는 환불이 불가합니다',
                  '환불 금액은 실제 결제 금액에서 이벤트 할인 혜택분을 제외한 정상가 기준으로 산정됩니다 (할인 혜택은 환불 대상에서 제외)',
                  '부가세(VAT 10%) 및 결제 수수료는 환불되지 않습니다',
                  '이용 기간에 비례하여 잔여 기간분만 환불됩니다 (일할 계산)',
                  '문의: lexojp.help@gmail.com',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-gray-600 text-[11px] mt-0.5 flex-shrink-0">•</span>
                    <span className="text-[11px] leading-relaxed text-gray-500">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={() => setShowNotify(true)}
              className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-gray-600 hover:border-teal-500/50 text-gray-300 hover:text-teal-300 text-sm py-3 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-notification-3-line text-sm"></i>
              Smart Plan · Pro Plan 오픈 알림 신청
              <i className="ri-arrow-right-s-line text-sm"></i>
            </button>

            {onShowHowTo && (
              <button
                onClick={onShowHowTo}
                className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-gray-600 hover:border-teal-500/50 text-gray-300 hover:text-teal-300 text-sm py-3 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-route-line text-sm"></i>
                자세한 진행방식 보기
                <i className="ri-arrow-right-s-line text-sm"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 완료 화면 */}
      {showComplete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => { setShowComplete(false); onClose(); }}></div>
          <div className="relative bg-[#111111] border border-teal-500/40 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600"></div>
            <div className="px-6 py-8 text-center">
              <div className="relative w-20 h-20 mx-auto mb-5">
                <div className="absolute inset-0 bg-teal-500/20 rounded-full animate-ping"></div>
                <div className="relative w-20 h-20 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center shadow-lg shadow-teal-500/30">
                  <i className="ri-check-line text-white text-3xl"></i>
                </div>
              </div>
              <h3 className="text-white font-black text-xl mb-2">수강 신청 완료! 🎉</h3>
              <p className="text-teal-400 font-semibold text-sm mb-5">LEXO 일본어와 함께 강해지세요!</p>

              <div className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 border-2 border-teal-500/50 rounded-2xl p-4 mb-4 text-left">
                <p className="text-teal-400 text-xs font-bold uppercase tracking-widest mb-3 text-center">선택하신 플랜 &amp; 옵션</p>
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2">
                    <i className="ri-vip-crown-2-line text-teal-400 text-lg"></i>
                    <span className="text-white font-black text-xl">LEXO Basic 플랜</span>
                  </div>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${colorMap[selected.color].bg} border ${colorMap[selected.color].border}`}>
                    <i className={`${selected.icon} ${colorMap[selected.color].text} text-base`}></i>
                    <span className={`font-black text-base ${colorMap[selected.color].text}`}>{selected.name}</span>
                    <span className={`text-sm font-bold ${colorMap[selected.color].text} opacity-80`}>{selected.subtitle}</span>
                    <span className={`text-xs font-black px-2 py-0.5 rounded-full ${colorMap[selected.color].badge}`}>{selected.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <i className="ri-time-line text-gray-400 text-xs"></i>
                    <span className="text-gray-300 text-sm font-semibold">{selected.duration}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 mb-5 text-left space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">입금 금액</span>
                  <span className="text-teal-400 text-base font-black">{formattedVatPrice}</span>
                </div>
                {depositorName && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">입금자명</span>
                    <span className="text-white text-sm font-semibold">{depositorName}</span>
                  </div>
                )}
              </div>
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4 mb-5 text-left">
                <p className="text-teal-400 font-bold text-sm mb-2 flex items-center gap-1.5">
                  <i className="ri-arrow-right-circle-line"></i>
                  다음 단계
                </p>
                <ol className="space-y-2">
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <span className="text-teal-500 font-bold flex-shrink-0">1.</span>
                    신한은행 <span className="text-white font-semibold">110621532440</span> (이송희)으로 입금
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <span className="text-teal-500 font-bold flex-shrink-0">2.</span>
                    카카오톡 채널에 입금 완료 메시지 전송
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <span className="text-teal-500 font-bold flex-shrink-0">3.</span>
                    확인 후 <span className="text-teal-400 font-semibold">밤 12시 전후</span> 학습 링크 발송!
                  </li>
                </ol>
              </div>
              <p className="text-gray-500 text-xs mb-5">
                문의: <span className="text-gray-400">lexojp.help@gmail.com</span>
              </p>
              <button
                onClick={() => { setShowComplete(false); onClose(); }}
                className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold py-3.5 rounded-xl text-base transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                확인했습니다 👍
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
